import React, { ChangeEvent } from 'react';
import { Input, Form, Typography, Col, Space } from 'antd';
import { FieldConfig } from '../../types/fieldTypes';
import { useValidator } from '../../hooks/useValidator';

const { TextArea } = Input;
const { Text } = Typography;

interface TextAreaFieldProps {
  field: FieldConfig;
  value: any;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  mode: 'edit' | 'view';
  formData?: Record<string, any>;
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  field,
  value,
  onChange,
  mode,
  formData = {},
}) => {
  const { touched, validationError, handleBlur, handleValidate } = useValidator(
    { field, value, formData }
  );

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const maxLength = field.customProps?.maxLength;
    if (maxLength && e.target.value.length > maxLength) {
      return;
    }

    onChange(e);
    handleValidate(e.target.value);

    // Call custom callbacks if they exist
    if (field.customProps?.onFieldChange) {
      field.customProps.onFieldChange(e.target.value, field.name, formData);
    }

    if (field.customProps?.onFormChange) {
      const updatedFormData = { ...formData, [field.name]: e.target.value };
      field.customProps.onFormChange(updatedFormData, field.name);
    }
  };

  const showCharCount = field.customProps?.showCharCount;
  const maxLength = field.customProps?.maxLength;

  if (mode === 'view') {
    return (
      <Col span={field.layout?.span || 24}>
        <Form.Item label={<Text type="secondary">{field.label}</Text>}>
          <TextArea value={value || 'N/A'} disabled autoSize={{ minRows: 3 }} />
        </Form.Item>
      </Col>
    );
  }

  const colProps = {
    span: field.layout?.span || 24,
    offset: field.layout?.offset || 0,
    order: field.layout?.order,
  };

  return (
    <Col {...colProps} className={field.layout?.className}>
      <Form.Item
        label={field.label}
        required={field.required}
        validateStatus={validationError && touched ? 'error' : ''}
        help={
          <Space direction="vertical" size="small">
            {field.customProps?.description && (
              <Text type="secondary">{field.customProps.description}</Text>
            )}
            {validationError && touched && (
              <Text type="danger">{validationError}</Text>
            )}
          </Space>
        }
      >
        <TextArea
          name={field.name}
          placeholder={field.customProps?.placeholder || field.placeholder}
          value={value || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          status={validationError && touched ? 'error' : ''}
          rows={field.customProps?.rows || 4}
          maxLength={maxLength}
          showCount={showCharCount}
          size="large"
        />
      </Form.Item>
    </Col>
  );
};
